<template>
  <div
    style="position: relative; overflow: auto; max-height: inherit"
    :style="{
      maxWidth: 15 * gridUnitSize + 'px'
    }"
  >
    <table :style="cssProps">
      <!--Top Header-->
      <thead>
        <tr>
          <th class="invisible" />
          <th v-for="column in numberOfColumns" :key="column">
            {{ column }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, rowIndex) in gridUnits" :key="rowIndex">
          <!--Left Header-->
          <th>{{ rowIndex + 1 }}</th>

          <!--Grid body-->
          <td
            v-for="gridUnit in row"
            :key="gridUnit.rowIndex + gridUnit.columnIndex"
            :class="{
              disabled: disabled,
              occupied: gridUnitHasDuctEntry(gridUnit)
            }"
            @click="onGridUnitClick(gridUnit)"
          >
            <grid-duct-entry
              v-if="gridUnitHasDuctEntry(gridUnit)"
              :duct-entry-index="getGridUnitDuctEntryIndex(gridUnit)"
              @showContextMenu="showContextMenu($event, gridUnit)"
              @hideContextMenu="hideContextMenu"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <duct-entry-menu
      :show-menu="showMenu"
      :x="menuX"
      :y="menuY"
      :duct-entry-index="clickedDuctEntryIndex"
      @delete="deleteDuctEntry"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { ductEntryTypesMixin } from "@/mixins/forms/masks/ductEntryTypesMixin";
import { maskTypesMixin } from "@/mixins/forms/masks/maskTypesMixin";
import GridDuctEntry from "../Duct/GridDuctEntry";
import DuctEntryMenu from "../Duct/DuctEntryMenu";
import _ from "lodash";
export default {
  name: "MaskGrid",

  components: { DuctEntryMenu, GridDuctEntry },

  mixins: [ductEntryTypesMixin, maskTypesMixin],

  data() {
    return {
      numberOfColumns: 12,
      numberOfRows: 12,
      showMenu: false,
      menuX: 0,
      menuY: 0,
      clickedDuctEntryIndex: undefined
    };
  },

  computed: {
    disabled() {
      return this.$store.getters.isManholeMasksTabDisabled;
    },

    gridUnitSize() {
      return this.$store.getters.getMaskGridUnitSize;
    },

    gridUnits() {
      return this.$store.getters.getMaskGridUnits;
    },

    cssProps() {
      return {
        "--grid-unit-size": this.gridUnitSize + "px"
      };
    },

    ductEntryMode() {
      return this.$store.getters.getMaskDuctEntryMode;
    },

    selectedDuctEntry() {
      return this.$store.getters.getSelectedDuctEntry;
    },

    selectedMaskType() {
      if (this.maskTypes && this.maskTypes.length > 0 && this.selectedMask) {
        return this.getMaskType(this.selectedMask.mk_type);
      } else
        return {
          horizontal_units: 12,
          vertical_units: 12
        };
    },

    selectedDuctEntryType() {
      return this.$store.getters.getSelectedDuctEntryType;
    }
  },

  watch: {
    "selectedMask.label"() {
      this.setGridUnits();
    },

    selectedDuctEntryType() {
      if (this.ductEntryMode === "resize") {
        this.updateDuctEntryType();
      }
    },

    selectedMaskType(newType) {
      if (newType) {
        this.numberOfColumns = parseInt(newType.vertical_units);
        this.numberOfRows = parseInt(newType.horizontal_units);
        this.setGridUnits();
      } else {
        this.$nextTick(() => this.setMasksTabLoading(false));
      }
    }
  },

  mounted() {
    this.setGridUnits();
  },

  methods: {
    ...mapActions([
      "addDuctEntry",
      "editDuctEntry",
      "removeDuctEntry",
      "setMaskGridUnits",
      "setMasksTabLoading"
    ]),
    /////////////////
    /* Prepare Grid*/
    /////////////////

    async setGridUnits() {
      this.$nextTick(() => {
        this.setMasksTabLoading(true);
      });
      let gridUnits = [];
      for (let i = 0; i < this.numberOfRows; i++) {
        gridUnits.push([]);
        for (let j = 0; j < this.numberOfColumns; j++) {
          let gridUnit = this.createGridUnit(i, j);
          gridUnits[i].push(gridUnit);
        }
      }
      await this.setMaskGridUnits(gridUnits);
      if (this.ductEntryTypes.length > 0) {
        await this.updateOccupiedGridUnits();
      } else {
        this.$nextTick(() => {
          this.setMasksTabLoading(false);
        });
      }
    },

    createGridUnit(rowIndex, columnIndex) {
      return {
        rowIndex: rowIndex,
        columnIndex: columnIndex,
        occupied: false
      };
    },

    getGridUnitByDuctEntryPosition(ductEntry) {
      let columnIndex = ductEntry.mde_top;
      let rowIndex = ductEntry.mde_left;
      return this.gridUnits[rowIndex][columnIndex];
    },

    updateOccupiedGridUnits() {
      if (
        this.selectedMask &&
        this.selectedMask.duct_entries &&
        this.selectedMask.duct_entries.length > 0
      ) {
        this.selectedMask.duct_entries.forEach(ductEntry => {
          this.updateDuctEntryAreaOccupation(ductEntry, true);
        });
      }
      this.$nextTick(() => {
        this.setMasksTabLoading(false);
      });
    },

    async updateDuctEntryAreaOccupation(ductEntry, occupy) {
      let ductEntryType = this.getDuctEntryType(ductEntry.mde_entry_type);
      let gridSectionInfo = {
        originColumnIndex: ductEntry.mde_top,
        originRowIndex: ductEntry.mde_left,
        numberOfColumns: ductEntryType.containerWidth,
        numberOfRows: ductEntryType.containerHeight
      };
      await this.changeOccupationOfGridUnits(gridSectionInfo, occupy);
    },

    ////////////////////////
    /* Manage Duct Entries*/
    ////////////////////////

    onGridUnitClick(gridUnit) {
      switch (this.ductEntryMode) {
        case "create":
          this.createDuctEntry(gridUnit);
          break;
        case "clone":
          this.cloneDuctEntry(gridUnit);
          break;
        case "move":
          this.moveDuctEntry(gridUnit);
          break;
      }
    },

    createDuctEntry(gridUnit) {
      if (this.selectedDuctEntryType) {
        let ductEntryIsPlaced = this.placeDuctEntry(
          gridUnit,
          this.selectedDuctEntryType
        );

        if (ductEntryIsPlaced) {
          let label = this.selectedMask.mk_label
            ? this.selectedMask.mk_label
            : this.selectedMask.code;
          let newDuctEntry = {
            mde_label: label + this.getNewDuctEntryNumber(),
            mde_default_destination: 0,
            mde_destination_specific_manhole_uuid: null,
            mde_destination_specific_mask: null,
            mde_state: 1,
            mde_total_reservation: false,
            mde_occupancy_rate: null,
            duct_entry_cables: [],
            mde_duct_id: null,
            mde_top: gridUnit.columnIndex,
            mde_left: gridUnit.rowIndex,
            mde_entry_type: this.selectedDuctEntryType.code
          };
          this.addDuctEntry(newDuctEntry);
        }
      }
    },

    cloneDuctEntry(gridUnit) {
      if (this.selectedDuctEntry) {
        let ductEntryIsPlaced = this.placeDuctEntry(
          gridUnit,
          this.getDuctEntryType(this.selectedDuctEntry.mde_entry_type)
        );

        if (ductEntryIsPlaced) {
          let newDuctEntry = {
            mde_label:
              this.selectedMask.mk_label + this.getNewDuctEntryNumber(),
            mde_default_destination: this.selectedDuctEntry
              .mde_default_destination,
            mde_destination_specific_manhole_uuid: this.selectedDuctEntry
              .mde_destination_specific_manhole_uuid,
            mde_destination_specific_mask: this.selectedDuctEntry
              .mde_destination_specific_mask,

            mde_state: this.selectedDuctEntry.mde_state,
            mde_total_reservation: this.selectedDuctEntry.mde_total_reservation,
            mde_occupancy_rate: null,
            duct_entry_cables: [],
            mde_duct_id: null,

            mde_top: gridUnit.columnIndex,
            mde_left: gridUnit.rowIndex,
            mde_entry_type: this.selectedDuctEntry.mde_entry_type
          };
          this.addDuctEntry(newDuctEntry);
        }
      }
    },

    getNewDuctEntryNumber() {
      let ductEntryNumbers = this.getListOfDuctEntryNumbers();
      let lastDuctEntryNumber;
      if (ductEntryNumbers && ductEntryNumbers.length > 0) {
        lastDuctEntryNumber = Math.max.apply(null, ductEntryNumbers);
      } else {
        lastDuctEntryNumber = 0;
      }
      return lastDuctEntryNumber + 1;
    },

    getListOfDuctEntryNumbers() {
      let ductEntryNumbers = [];
      if (this.selectedMask.duct_entries) {
        this.selectedMask.duct_entries.forEach(ductEntry => {
          let labelNumber = this.getDuctEntryLabelNumber(ductEntry.mde_label);
          ductEntryNumbers.push(labelNumber);
        });
      }
      return ductEntryNumbers;
    },

    getDuctEntryLabelNumber(label) {
      let labelNumber = label.slice(1);
      return parseInt(labelNumber);
    },

    moveDuctEntry(destinationGridUnit) {
      if (this.selectedDuctEntry) {
        let ductEntry = this.selectedDuctEntry;
        let ductEntryType = this.getDuctEntryType(ductEntry.mde_entry_type);
        let ductEntryIsPlaced = this.placeDuctEntry(
          destinationGridUnit,
          ductEntryType
        );
        if (ductEntryIsPlaced) {
          let originalGridUnit = this.getGridUnitByDuctEntryPosition(ductEntry);
          let originalGridSection = this.getGridSectionInfo(
            originalGridUnit,
            ductEntryType
          );
          this.changeOccupationOfGridUnits(originalGridSection, false);
          let dataToUpdate = [
            { field: "mde_top", value: destinationGridUnit.columnIndex },
            { field: "mde_left", value: destinationGridUnit.rowIndex }
          ];
          this.editDuctEntry(dataToUpdate);
        }
      }
    },

    placeDuctEntry(gridUnit, ductEntryType) {
      let gridSectionInfo = this.getGridSectionInfo(gridUnit, ductEntryType);

      let occupationIsPossible = this.gridUnitsAreUnoccupied(gridSectionInfo);

      if (occupationIsPossible) {
        this.changeOccupationOfGridUnits(gridSectionInfo, true);
        return true;
      } else return false;
    },

    gridUnitsAreUnoccupied(gridSectionInfo) {
      let gridUnit;
      for (let i = 0; i < gridSectionInfo.numberOfRows; i++) {
        let row = this.gridUnits[gridSectionInfo.originRowIndex + i];

        if (row) {
          for (let j = 0; j < gridSectionInfo.numberOfColumns; j++) {
            gridUnit = row[gridSectionInfo.originColumnIndex + j];
            if (!gridUnit || gridUnit.occupied) {
              return false;
            }
          }
        } else return false;
      }
      return true;
    },

    updateDuctEntryType() {
      let gridUnit = this.getGridUnitByDuctEntryPosition(
        this.selectedDuctEntry
      );
      let originalDuctEntryType = this.getDuctEntryType(
        this.selectedDuctEntry.mde_entry_type
      );
      let newDuctEntryType = this.selectedDuctEntryType;

      if (
        this.canChangeDuctEntryType(
          gridUnit,
          originalDuctEntryType,
          newDuctEntryType
        )
      ) {
        let originalGridSection = this.getGridSectionInfo(
          gridUnit,
          originalDuctEntryType
        );
        this.changeOccupationOfGridUnits(originalGridSection, false);

        let newGridSection = this.getGridSectionInfo(
          gridUnit,
          newDuctEntryType
        );
        this.changeOccupationOfGridUnits(newGridSection, true);
        let dataToUpdate = [
          { field: "mde_entry_type", value: newDuctEntryType.code }
        ];
        this.editDuctEntry(dataToUpdate);
      }
    },

    canChangeDuctEntryType(
      originGridUnit,
      originalDuctEntryType,
      newDuctEntryType
    ) {
      let originalWidth = originalDuctEntryType.containerWidth;
      let newWidth = newDuctEntryType.containerWidth;
      let originalHeight = originalDuctEntryType.containerHeight;
      let newHeight = newDuctEntryType.containerHeight;

      if (newWidth > originalWidth || newHeight > originalHeight) {
        let gridUnit;
        for (let i = 0; i < newHeight; i++) {
          let row = this.gridUnits[originGridUnit.rowIndex + i];
          if (row) {
            for (let j = 0; j < newWidth; j++) {
              if (i >= originalHeight || j >= originalWidth) {
                gridUnit = row[originGridUnit.columnIndex + j];
                if (!gridUnit || gridUnit.occupied) {
                  return false;
                }
              }
            }
          } else return false;
        }
        return true;
      } else return true;
    },

    deleteDuctEntry(selectedDuctEntryIndex) {
      let selectedDuctEntry = this.$store.getters.getDuctEntryByIndex(
        selectedDuctEntryIndex
      );
      let gridUnit = this.getGridUnitByDuctEntryPosition(selectedDuctEntry);
      let gridSection = this.getGridSectionInfo(gridUnit, selectedDuctEntry);
      this.changeOccupationOfGridUnits(gridSection, false);
      this.removeDuctEntry(selectedDuctEntryIndex);
    },

    getGridSectionInfo(gridUnitOfOrigin, ductEntryType) {
      return {
        originColumnIndex: gridUnitOfOrigin.columnIndex,
        originRowIndex: gridUnitOfOrigin.rowIndex,
        numberOfColumns: ductEntryType.containerWidth,
        numberOfRows: ductEntryType.containerHeight
      };
    },

    async changeOccupationOfGridUnits(gridSectionInfo, occupy) {
      let originRow = gridSectionInfo.originRowIndex;
      let originColumn = gridSectionInfo.originColumnIndex;

      let gridUnits = _.cloneDeep(this.gridUnits);
      for (let i = 0; i < gridSectionInfo.numberOfRows; i++) {
        for (let j = 0; j < gridSectionInfo.numberOfColumns; j++) {
          gridUnits[originRow + i][originColumn + j].occupied = occupy;
        }
      }
      await this.setMaskGridUnits(gridUnits);
    },

    showContextMenu(e, gridUnit) {
      e.preventDefault();
      if (!this.disabled) {
        this.showMenu = false;
        this.menuX = e.clientX;
        this.menuY = e.clientY;
        this.clickedDuctEntryIndex = this.getGridUnitDuctEntryIndex(gridUnit);
        this.$nextTick(() => {
          this.showMenu = true;
        });
      }
    },

    hideContextMenu() {
      this.showMenu = false;
      this.clickedDuctEntryIndex = undefined;
    }
  }
};
</script>

<style scoped>
table {
  border-collapse: collapse;
  margin: auto;
}
th,
td {
  min-width: var(--grid-unit-size);
  height: var(--grid-unit-size);
  border: 1px solid lightgrey;
  background-color: white;
  text-align: center;
}
td {
  vertical-align: top;
}
td:not(.disabled):not(.occupied):hover {
  background-color: rgba(79, 162, 183, 0.1);
}

td:disabled {
  pointer-events: none;
}

thead th {
  position: -webkit-sticky; /* for Safari */
  position: sticky;
  top: 0;
}

tbody th {
  position: -webkit-sticky; /* for Safari */
  position: sticky;
  left: 0;
}

.invisible {
  border: none;
  background-color: transparent;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/_SpecialTabs/Masks/Masks/MaskGrid.vue